import React from 'react';
import { alpha } from '@mui/material/styles';
import moment from 'moment';
import {
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Event as EventIcon,
  Login as CheckInIcon,
  Logout as CheckOutIcon,
  MailOutline as MailOutlineIcon,
  Payments as PaymentsIcon,
  Person as PersonIcon,
  Room as RoomIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import {
  BOOKING_CHECKED_IN_COLOR,
  BOOKING_CONFIRMED_COLOR,
} from './bookingOpsCardHelpers';
import {
  BOOKING_DRAWER_ACCENT,
  getBookingDrawerHeaderSx,
  getBookingDrawerMetaChipSx,
  getBookingDrawerPanelSx,
  getBookingDrawerTheme,
} from './bookingDrawerLayout';

const PAYMENT_EVENT_COLOR = '#f59e0b';
const CHECKED_OUT_COLOR = '#9e9e9e';
const CANCELLED_COLOR = '#ef5350';
const GUEST_EVENT_COLOR = '#6366f1';

export function sanitizeBookingAuditText(value) {
  if (value == null) return '';
  return String(value)
    .replace(/\bvia\s+aiosell\s+webhook\b/gi, 'via webhook')
    .replace(/\baiosell\s+webhook\b/gi, 'webhook')
    .replace(/\baiosell\b/gi, 'webhook')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

export function sanitizeBookingAuditActor(value) {
  const text = String(value || '').trim();
  if (!text) return 'webhook';
  if (/^aiosell$/i.test(text)) return 'webhook';
  return sanitizeBookingAuditText(text) || 'webhook';
}

export function formatBookingAuditTime(createdAt, createdAtDisplay) {
  if (createdAtDisplay && String(createdAtDisplay).trim()) return createdAtDisplay;
  if (!createdAt) return '—';
  const parsed = moment(createdAt);
  if (!parsed.isValid()) return '—';
  return parsed.format('DD MMM YYYY, hh:mm A');
}

export function getBookingAuditEventPresentation(eventType) {
  const normalized = String(eventType || '').toLowerCase();

  if (['created', 'confirmed', 'booking_status_changed'].includes(normalized)) {
    return { icon: <CheckCircleIcon sx={{ fontSize: 18 }} />, color: BOOKING_CONFIRMED_COLOR };
  }
  if (['checked_in', 'partial_checkin'].includes(normalized)) {
    return { icon: <CheckInIcon sx={{ fontSize: 18 }} />, color: BOOKING_CHECKED_IN_COLOR };
  }
  if (['checked_out', 'partial_checkout'].includes(normalized)) {
    return { icon: <CheckOutIcon sx={{ fontSize: 18 }} />, color: CHECKED_OUT_COLOR };
  }
  if ([
    'payment_added',
    'payment_updated',
    'payment_deleted',
    'extra_charges_added',
    'extra_charges_updated',
    'extra_charges_removed',
  ].includes(normalized)) {
    return { icon: <PaymentsIcon sx={{ fontSize: 18 }} />, color: PAYMENT_EVENT_COLOR };
  }
  if (normalized.startsWith('communication_')) {
    return { icon: <MailOutlineIcon sx={{ fontSize: 18 }} />, color: BOOKING_DRAWER_ACCENT };
  }
  if (['room_changed', 'room_upgraded', 'room_added', 'rooms_allocated'].includes(normalized)) {
    return { icon: <RoomIcon sx={{ fontSize: 18 }} />, color: BOOKING_DRAWER_ACCENT };
  }
  if (['guest_added', 'guest_updated', 'guest_removed', 'guest_rematched'].includes(normalized)) {
    return { icon: <PersonIcon sx={{ fontSize: 18 }} />, color: GUEST_EVENT_COLOR };
  }
  if (['checkout_date_extended', 'checkout_date_shortened', 'booking_modified', 'converted_hourly_to_nightly'].includes(normalized)) {
    return { icon: <TimelineIcon sx={{ fontSize: 18 }} />, color: BOOKING_DRAWER_ACCENT };
  }
  if (['cancelled', 'no_show_marked', 'no_show_ota_failed', 'no_show_ota_success'].includes(normalized)) {
    return { icon: <CancelIcon sx={{ fontSize: 18 }} />, color: CANCELLED_COLOR };
  }
  return { icon: <EventIcon sx={{ fontSize: 18 }} />, color: BOOKING_DRAWER_ACCENT };
}

export function getBookingAuditDrawerPaperSx(isDark, extra = {}) {
  const tokens = getBookingDrawerTheme(isDark);
  return {
    width: { xs: '100%', sm: 600 },
    background: isDark ? tokens.surfaceGradient : '#ffffff',
    backgroundColor: isDark ? tokens.surface : '#ffffff',
    borderLeft: isDark ? `1px solid ${tokens.surfaceBorder}` : undefined,
    ...extra,
  };
}

export function getBookingAuditDrawerHeaderSx(isDark) {
  return {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    p: 1.5,
    ...getBookingDrawerHeaderSx(isDark),
  };
}

export function getBookingAuditRefreshRowSx(isDark) {
  const tokens = getBookingDrawerTheme(isDark);
  return {
    p: 2,
    borderBottom: `1px solid ${tokens.surfaceBorder}`,
  };
}

export function getBookingAuditRefreshButtonSx(isDark) {
  return {
    color: BOOKING_DRAWER_ACCENT,
    textTransform: 'none',
    fontWeight: 600,
    p: 0,
    minWidth: 0,
    '&:hover': {
      backgroundColor: 'transparent',
      color: BOOKING_DRAWER_ACCENT,
      opacity: 0.86,
    },
    '&.Mui-disabled': {
      color: alpha(getBookingDrawerTheme(isDark).subtleText, 0.55),
    },
  };
}

export function getBookingAuditEmptyStateSx(isDark) {
  const tokens = getBookingDrawerTheme(isDark);
  return getBookingDrawerPanelSx(isDark, {
    p: 2,
    borderRadius: 2,
    borderStyle: 'dashed',
    textAlign: 'center',
    backgroundColor: isDark ? tokens.chipBg : '#ffffff',
  });
}

export function getBookingAuditTimelineConnectorSx(isDark) {
  const tokens = getBookingDrawerTheme(isDark);
  return {
    mt: 0.7,
    width: 2,
    flex: 1,
    minHeight: 38,
    bgcolor: tokens.surfaceBorder,
  };
}

export function getBookingAuditTimelineAvatarSx(isDark, color) {
  const tokens = getBookingDrawerTheme(isDark);
  return {
    width: 28,
    height: 28,
    bgcolor: isDark ? alpha(color, 0.12) : alpha(color, 0.08),
    color,
    border: `1px solid ${alpha(color, isDark ? 0.45 : 0.35)}`,
  };
}

export function getBookingAuditEventCardSx(isDark) {
  const tokens = getBookingDrawerTheme(isDark);
  return getBookingDrawerPanelSx(isDark, {
    flex: 1,
    p: 1.5,
    borderRadius: 2,
    backgroundColor: isDark ? tokens.chipBg : '#ffffff',
  });
}

export function getBookingAuditActorChipSx(isDark) {
  return {
    height: 22,
    fontSize: '0.68rem',
    ...getBookingDrawerMetaChipSx(isDark),
  };
}

export function getBookingAuditMetaChipSx(isDark) {
  const tokens = getBookingDrawerTheme(isDark);
  return {
    height: 22,
    fontSize: '0.68rem',
    backgroundColor: isDark ? alpha('#ffffff', 0.06) : alpha('#000', 0.04),
    color: tokens.subtleText,
    border: `1px solid ${tokens.surfaceBorder}`,
  };
}

export function getBookingAuditEventTypeChipSx(isDark, color) {
  return {
    height: 22,
    fontSize: '0.68rem',
    textTransform: 'capitalize',
    backgroundColor: isDark ? alpha(color, 0.14) : alpha(color, 0.1),
    color: isDark ? alpha(color, 0.95) : color,
    border: `1px solid ${alpha(color, isDark ? 0.35 : 0.28)}`,
  };
}
